import { CommonModule, DecimalPipe } from '@angular/common';
import {
  Component,
  effect,
  EventEmitter,
  inject,
  Input,
  Output,
  Signal,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatCardModule } from '@angular/material/card';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { FilterCombinationResponse } from '@api-response-types/filter-combination.response';
import {
  ForecastSeriesCompletedResponse,
  ForecastSeriesCompletedWithoutMeasure,
} from '@api-response-types/forecast-series.response';
import { UpdatedForecastSeriesResponse } from '@api-response-types/updated-forecast-series.response';
import { Resolution } from '@api-types/enums/resolution.enum';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import Color from 'color';
import { differenceInDays, differenceInMilliseconds } from 'date-fns';
import { ChartOptionsBuilder } from 'demand-forecasting/components/chart/chart-options-builder';
import { ChartColorService } from 'demand-forecasting/services/chart/chart-color.service';
import { HighchartsUtilService } from 'demand-forecasting/services/chart/highcharts-util.service';
import { ForecastSeriesService } from 'demand-forecasting/services/forecast-series/forecast-series.service';
import { ConfigurationsSelectors } from 'demand-forecasting/store/configurations/configurations.selectors';
import {
  selectAllFilterCombinationForecastSeries,
  selectSavedFilterCombinations,
} from 'demand-forecasting/store/filter/filter.selectors';
import {
  latestCompletedForecastSeries,
  selectFixedYAxisZeroPoint,
  selectShowInterval,
} from 'demand-forecasting/store/forecast-series/forecast-series.selectors';
import {
  selectResolution,
  selectTargetUnit,
} from 'demand-forecasting/store/router-state/router.selectors';
import { DataSeries } from 'demand-forecasting/types/data-series.interface';
import Highcharts, {
  AxisSetExtremesEventObject,
  ExtremesObject,
  PatternObject,
  PointClickEventObject,
  SeriesOptionsType,
} from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';
import { debounceTime } from 'rxjs';

import {
  FORECAST_EXPORT_OPTIONS,
  FORECAST_PLOT_BAND,
  FORECAST_PLOT_LINE,
  FORECAST_RANGE_SELECTOR,
  handleChartExport,
} from 'demand-forecasting/types/forecast-chart/forecast-chart-options';
import { ForecastSeriesTypesEnum } from 'demand-forecasting/types/forecast-chart/forecast-series-types.enum';
import { HighchartsOptions } from 'demand-forecasting/types/forecast-chart/highcharts-options';
import { getMinAndMaxDate, getPlotbandStartDate } from 'insights/utils';

interface TableColumn {
  tstamp: string;
  actual: number;
  forecast: number;
  confidence_min?: number;
  confidence_max?: number;
}

type FilterCombinationForecastSeriesProps = {
  filterCombinationForecastSeries: UpdatedForecastSeriesResponse;
  resolution: Resolution;
  series: SeriesOptionsType[];
  filterCombinationId: string;
  savedFilterCombinations: FilterCombinationResponse[];
  color: string;
};

export type ClickedPredictionPoint = {
  date: number;
  /**
   * important to sync colors between different components like the prediction explanation
   */
  color: string;
  /**
   * in case the click was on a filter combination series, the filter combination id is provided here. Otherwise it is undefined.
   */
  filterCombinationId?: string;
};

@Component({
  selector: 'pm-forecast-chart',
  templateUrl: './forecast-chart.component.html',
  imports: [
    CommonModule,
    DecimalPipe,
    TranslateModule,
    HighchartsChartComponent,
    MatTableModule,
    MatSortModule,
    MatCardModule,
  ],
})

/**
 * Component that displays the forecast chart.
 * See documentation/forecast-chart/readme.md for more information.
 */
export class ForecastChartComponent {
  private readonly store = inject(Store);
  private readonly highchartsUtilService = inject(HighchartsUtilService);
  private readonly forecastSeriesService = inject(ForecastSeriesService);

  private readonly chartColorService = inject(ChartColorService);
  private readonly translationService = inject(TranslateService);

  // inputs & outputs
  /**
   * Whether the table should be allow to show or not
   */
  @Input() allowTableView = true;
  @Output() clickedPredictionPoint = new EventEmitter<ClickedPredictionPoint>();
  @Output() changedTimeRange = new EventEmitter<ExtremesObject>();

  // store variables
  private readonly forecastSeries = this.store.selectSignal(
    latestCompletedForecastSeries,
  );
  private readonly forecastResolution = this.store.selectSignal(
    selectResolution,
  ) as Signal<Resolution | undefined>;
  private readonly targetUnit = this.store.selectSignal(selectTargetUnit);

  private readonly isIntervalEnabled =
    this.store.selectSignal(selectShowInterval);
  private readonly isSelectFixedYAxisZeroPointEnabled = this.store.selectSignal(
    selectFixedYAxisZeroPoint,
  );

  private readonly additionalTimeSeries = this.store.selectSignal(
    ConfigurationsSelectors.selectAdditionalTimeSeriesNamesByUrlParam,
  );

  private readonly filterCombinationTimeSeries = toSignal(
    this.store
      .select(selectAllFilterCombinationForecastSeries)
      .pipe(debounceTime(100)),
    {
      initialValue: {},
    },
  );

  private readonly savedFilterCombinations = this.store.selectSignal(
    selectSavedFilterCombinations,
  );

  // chart variables
  protected chartOptions = signal<HighchartsOptions>(
    new ChartOptionsBuilder().build(),
  );

  // table variables
  isTableVisible = signal(false);
  tableData: MatTableDataSource<TableColumn>;

  @ViewChild(MatSort, { static: true }) set sort(sort: MatSort) {
    this.tableData.sort = sort;
    this.tableData.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'tstamp':
          return new Date(item.tstamp).getTime();
        case 'actual':
          return item.actual;
        case 'forecast':
          return item.forecast;
        case 'confidence_min':
          return item.confidence_min ?? -1;
        case 'confidence_max':
          return item.confidence_max ?? -1;
        default:
          return '';
      }
    };
  }

  protected displayedTableColumns = [
    'tstamp',
    'actual',
    'forecast',
    'confidence_min',
    'confidence_max',
  ];

  constructor() {
    this.highchartsUtilService
      .setHighchartLanguage()
      .pipe(takeUntilDestroyed())
      .subscribe();
    this.tableData = new MatTableDataSource();
    effect(() => {
      const chartOptions = this.chartOptions();
      const resolution = this.forecastResolution();
      const dateFormat = this.highchartsUtilService.getDateFormat(resolution);
      this.tableData.data = this.prepareTableData(chartOptions, dateFormat);
    });

    effect(() => {
      const forecastSeries = this.forecastSeries();
      const targetUnitUpdated = this.targetUnit();
      const resolution = this.forecastResolution();
      const isSelectFixedYAxisZeroPointEnabled =
        this.isSelectFixedYAxisZeroPointEnabled();
      const isIntervalEnabled = this.isIntervalEnabled();
      if (!forecastSeries) {
        console.error('No forecast series available');
        return;
      }

      if (!this.validateForecastSeriesDataLengths(forecastSeries)) {
        console.error('Forecast series data arrays have mismatched lengths');
        return;
      }

      const validIndexes = this.getValidIndexes(forecastSeries);
      const filteredForecastSeries = this.filterForecastSeriesByIndexes(
        forecastSeries,
        validIndexes,
      );

      const options = this.createChartOptions(
        filteredForecastSeries,
        targetUnitUpdated,
        resolution,
        isIntervalEnabled,
        isSelectFixedYAxisZeroPointEnabled,
      );
      this.chartOptions.set(options);
    });
  }

  private validateForecastSeriesDataLengths(
    forecastSeries: ForecastSeriesCompletedWithoutMeasure,
  ): boolean {
    const dataLength = forecastSeries.predictions.data.length;
    return (
      forecastSeries.values.data.length === dataLength &&
      forecastSeries.confidences.data.length === dataLength &&
      forecastSeries.originalDates.length === dataLength
    );
  }

  private getValidIndexes(
    forecastSeries: ForecastSeriesCompletedWithoutMeasure,
  ): number[] {
    // Get valid indexes where actuals or predictions have non-null values inside their respective ranges
    const validIndexes: number[] = [];
    forecastSeries.values.data.forEach((series, index) => {
      const isPredictionData = index >= forecastSeries.initialForecastIndex;
      if (isPredictionData) {
        return;
      }
      const hasValue = series[1] !== null;
      if (hasValue) {
        validIndexes.push(index);
      }
    });
    forecastSeries.predictions.data.forEach((series, index) => {
      const isActualsData = index < forecastSeries.initialForecastIndex;
      if (isActualsData) {
        return;
      }
      const hasPredictionValue = series[1] !== null;

      if (hasPredictionValue) {
        validIndexes.push(index);
      }
    });
    return validIndexes;
  }

  private filterForecastSeriesByIndexes(
    forecastSeries: ForecastSeriesCompletedWithoutMeasure,
    validIndexes: number[],
  ): ForecastSeriesCompletedWithoutMeasure {
    const filteredPredictionsData: typeof forecastSeries.predictions.data = [];
    const filteredValuesData: typeof forecastSeries.values.data = [];
    const filteredConfidencesData: typeof forecastSeries.confidences.data = [];
    const filteredOriginalDates: typeof forecastSeries.originalDates = [];

    for (const i of validIndexes) {
      filteredPredictionsData.push(forecastSeries.predictions.data[i]);
      filteredValuesData.push(forecastSeries.values.data[i]);
      filteredConfidencesData.push(forecastSeries.confidences.data[i]);
      filteredOriginalDates.push(forecastSeries.originalDates[i]);
    }

    // Calculate the new initialForecastIndex based on how many valid indexes come before the original initialForecastIndex
    const newInitialForecastIndex = validIndexes.filter(
      (index) => index < forecastSeries.initialForecastIndex,
    ).length;

    return {
      ...forecastSeries,
      predictions: {
        ...forecastSeries.predictions,
        data: filteredPredictionsData,
      },
      values: {
        ...forecastSeries.values,
        data: filteredValuesData,
      },
      confidences: {
        ...forecastSeries.confidences,
        data: filteredConfidencesData,
      },
      originalDates: filteredOriginalDates,
      initialForecastIndex: newInitialForecastIndex,
    };
  }

  private prepareTableData(
    chartOptions: HighchartsOptions,
    dateFormat: string,
  ): TableColumn[] {
    if (chartOptions && chartOptions.series && chartOptions.series.length > 0) {
      let tmpData: TableColumn[] = [];

      const findSeriesById = (needle: string) =>
        chartOptions.series?.find(
          (series) => !!series.id && series.id.indexOf(needle) > -1,
        ) as (SeriesOptionsType & { data: number[][] }) | undefined;

      const actualsSeries = findSeriesById('actual');
      const incompleteSeries = findSeriesById('incomplete');
      const forecasts = findSeriesById('prediction');
      const confidence = findSeriesById('confidence');

      if (!actualsSeries || !forecasts) {
        return [];
      }

      const actualsData = [...actualsSeries.data];
      if (incompleteSeries && incompleteSeries.data.length > 0) {
        actualsData.push(incompleteSeries.data[0]);
      }

      for (let index = 0; index < actualsData.length; index += 1) {
        let row: TableColumn;
        // TODO Implement logic to handle missing confidence data
        if (confidence && confidence.data[index]) {
          if (
            actualsData[index][0] !== forecasts.data[index][0] ||
            actualsData[index][0] !== confidence.data[index][0]
          ) {
            // Continue if there is a mismatch in the timestamp of corresponding arrays

            continue;
          }
          row = {
            tstamp: Highcharts.dateFormat(dateFormat, actualsData[index][0]),
            actual: actualsData[index][1],
            forecast: forecasts.data[index][1],
            confidence_min: confidence.data[index][1],
            confidence_max: confidence.data[index][2],
          };
        } else {
          row = {
            tstamp: Highcharts.dateFormat(dateFormat, actualsData[index][0]),
            actual: actualsData[index][1],
            forecast: forecasts.data[index][1],
          };
        }

        tmpData.push(row);
      }

      if (tmpData.length > 0) {
        // Initial Sorting Tstamp latest on top
        tmpData = tmpData.sort((a, b) => (a < b ? 1 : -1));
        return tmpData;
      }
    }
    return [];
  }

  private createChartOptions(
    forecastSeries: ForecastSeriesCompletedWithoutMeasure,
    translatedTargetUnitUpdated: string | undefined,
    resolution: Resolution | undefined,
    isIntervalEnabled: boolean,
    isSelectFixedYAxisZeroPointEnabled = true,
  ): HighchartsOptions {
    if (!resolution) {
      console.warn('No resolution available, rendering empty chart');
      return new ChartOptionsBuilder().build();
    }

    const forecastSeriesCopy = structuredClone(
      forecastSeries,
    ) as ForecastSeriesCompletedResponse;
    const dataSeries =
      this.forecastSeriesService.toSeriesData(forecastSeriesCopy);

    const translatedTargetUnit =
      translatedTargetUnitUpdated || forecastSeriesCopy.xAxis;

    const dateformat = this.highchartsUtilService.getDateFormat(resolution);

    const [, max] = getMinAndMaxDate(forecastSeriesCopy);
    const plotBandStartDate = getPlotbandStartDate(forecastSeriesCopy);

    const initialTimeRange =
      this.calculateInitialTimeRangeInDays(forecastSeriesCopy);
    const filterCombinationTimeSeries = this.filterCombinationTimeSeries();

    const emitDisplayedTimeRange = (chart: Highcharts.Chart): void => {
      this.changedTimeRange.emit(chart.xAxis[0].getExtremes());
    };

    const options = new ChartOptionsBuilder()
      .withSeries(this.getAdditionalTimeSeries(dataSeries, resolution))
      .withSeries(this.getConfidenceIntervalSeries(dataSeries, resolution))
      .withSeries(this.getActualsSeries(dataSeries, resolution))
      .withSeries(this.getPredictionsSeries(dataSeries, resolution))
      .withSeries(this.getIncompleteActuals(dataSeries, resolution))
      .withSeries(
        this.getFilterCombinationForecastSeries(filterCombinationTimeSeries),
      )
      .withXAxis({
        events: {
          afterSetExtremes: (event: AxisSetExtremesEventObject) => {
            this.changedTimeRange.emit(event);

            // Show/hide plot band label based on viewport visibility
            if (
              event.target &&
              event.min !== undefined &&
              event.max !== undefined
            ) {
              const axis = event.target as unknown as Highcharts.Axis;
              const chart = axis.chart;

              if (chart && chart.container) {
                // Check if plot band is visible in current viewport
                const isPlotBandVisible =
                  plotBandStartDate < event.max && max > event.min;

                // Find and update label visibility
                const labelElements = chart.container.querySelectorAll(
                  '.forecast-plot-band-label',
                );

                labelElements.forEach((labelElement: Element) => {
                  const parentElement = labelElement.parentElement;
                  if (parentElement) {
                    parentElement.style.display = isPlotBandVisible
                      ? ''
                      : 'none';
                  }
                });
              }
            }
          },
        },
        plotBands: [FORECAST_PLOT_BAND(plotBandStartDate, max)],
        plotLines: [FORECAST_PLOT_LINE(plotBandStartDate)],
        title: {
          text: forecastSeriesCopy.xAxis,
        },
        max,
        min: this.calculateMinDateToMatchShownRange(forecastSeriesCopy),
        minRange: 24 * 3600 * 1000, // 1 Day
      })
      .withNavigator({
        enabled: true,
        adaptToUpdatedData: true,
        stickToMax: false,
      })
      .withRangeSelector(FORECAST_RANGE_SELECTOR(initialTimeRange, resolution))
      .withExportingOptions(
        FORECAST_EXPORT_OPTIONS(
          this.highchartsUtilService.getAdditonalTranslations(),
          dateformat,
          forecastSeriesCopy.yAxis,
          this.switchTableView.bind(this),
        ),
      )
      .withPlotOptions({
        series: {
          allowPointSelect: true,
          cursor: 'pointer',
          point: {
            events: {
              click: (event: PointClickEventObject) => {
                this.clickedPredictionPoint.emit({
                  date: event.point.x,
                  color: dataSeries.colors[0],
                });
              },
            },
          },
          marker: {
            states: {
              select: {
                enabled: true,
              },
            },
          },
        },
        column: {
          stacking: undefined,
          grouping: true,
          events: {
            // ...this.hideNullValuesAccessor(),
          },
        },
      })
      .withChartOptions({
        animation: false,
        events: {
          exportData: handleChartExport,
          load(this: Highcharts.Chart) {
            emitDisplayedTimeRange(this);
          },
        },
      })
      .withYAxis({
        title: {
          text: translatedTargetUnit,
        },
        ...(isSelectFixedYAxisZeroPointEnabled && {
          min: 0,
        }),
      })
      .build();

    return options;
  }

  private switchTableView() {
    const prevVisibleState = this.isTableVisible();
    this.isTableVisible.set(!prevVisibleState);

    setTimeout(() => {
      if (this.isTableVisible()) {
        document.getElementById('data-table')?.scrollIntoView();
      } else {
        document.getElementById('result-chart')?.scrollIntoView();
      }
    }, 100);
  }

  private calculateMinDateToMatchShownRange(
    forecastSeries: ForecastSeriesCompletedResponse,
  ): number {
    const [, maxDate] = getMinAndMaxDate(forecastSeries);
    const initialForecastDate =
      forecastSeries.values.data[forecastSeries.initialForecastIndex - 1][0];
    return maxDate - differenceInMilliseconds(maxDate, initialForecastDate) * 2;
  }

  private calculateInitialTimeRangeInDays(
    forecastSeries: ForecastSeriesCompletedResponse,
  ): number {
    const [, maxDate] = getMinAndMaxDate(forecastSeries);
    const initialForecastDate =
      forecastSeries.values.data[forecastSeries.initialForecastIndex - 1][0];
    return differenceInDays(maxDate, initialForecastDate) * 2;
  }

  private getPredictionsSeries(
    dataSeries: DataSeries,
    resolution: Resolution,
  ): SeriesOptionsType[] {
    // prediction chart
    const predictions: SeriesOptionsType[] = [];

    // only show main series if no filter combination is active
    if (this.areFilterCombinationsActive()) {
      return predictions;
    }

    if (dataSeries.predictions) {
      predictions.push({
        ...dataSeries.predictions,
        dashStyle: resolution !== Resolution.Yearly ? 'ShortDot' : undefined,
        color:
          resolution === Resolution.Yearly
            ? this.transformColorForType(dataSeries.colors[0], 'prediction')
            : dataSeries.colors[0],
        zIndex: 1,
        showInNavigator: dataSeries.visible,
        type: resolution === Resolution.Yearly ? 'column' : 'spline',
        id: `prediction-${resolution}`,
        custom: { type: ForecastSeriesTypesEnum.AGGREGATION },
      });
    }

    if (dataSeries.whatIfPredictions) {
      predictions.push({
        id: `what-if-prediction-${resolution}`,
        type: 'spline',
        ...dataSeries.whatIfPredictions,
        showInNavigator: false,
        color: dataSeries.whatIfPredictions.color,
        zIndex: 6,
        custom: {
          type: ForecastSeriesTypesEnum.PREDICTION,
        },
      });
    }

    return predictions;
  }

  private getConfidenceIntervalSeries(
    dataSeries: DataSeries,
    resolution: Resolution,
  ): SeriesOptionsType[] {
    const confidences: SeriesOptionsType[] = [];

    // only show main series if no filter combination is active
    if (this.areFilterCombinationsActive()) {
      return confidences;
    }

    if (dataSeries.confidences && this.isIntervalEnabled()) {
      confidences.push({
        ...dataSeries.confidences,
        name: dataSeries.confidences.name,
        color:
          resolution === Resolution.Yearly
            ? this.transformColorForType(dataSeries.colors[0], 'confidence')
            : dataSeries.colors[0],
        zIndex: 1,
        opacity: resolution === Resolution.Yearly ? 1 : 0.3,
        type: resolution === Resolution.Yearly ? 'errorbar' : 'areasplinerange',
        marker: {
          enabled: false,
          states: {
            hover: {
              enabled: false,
            },
          },
        },
        allowPointSelect: false,
        id: `confidence-${resolution}`,
      });
    }

    if (dataSeries.whatIfConfidences) {
      confidences.push({
        id: `what-if-confidence-${resolution}`,
        ...dataSeries.whatIfConfidences,
        color: dataSeries.whatIfPredictions?.color,
        zIndex: 4,
        opacity: 0.3,
        type: resolution === Resolution.Yearly ? 'errorbar' : 'areasplinerange',
        allowPointSelect: false,
      });
    }
    return confidences;
  }

  private getActualsSeries(
    dataSeries: DataSeries,
    resolution: Resolution,
  ): SeriesOptionsType[] {
    const actuals: SeriesOptionsType[] = [];

    // only show main series if no filter combination is active
    if (this.areFilterCombinationsActive()) {
      return actuals;
    }

    // truth chart
    actuals.push({
      name: dataSeries.values.name,
      data: dataSeries.values.incompleteTruth
        ? dataSeries.values.data
            .filter((value) => value[1] !== null)
            .slice(0, -1)
        : dataSeries.values.data, // remove last truth value if it is incomplete
      visible: dataSeries.values.visible,
      color: this.transformColorForType(dataSeries.colors[0], 'actual'),
      showInNavigator: dataSeries.values.visible,
      type: resolution === Resolution.Yearly ? 'column' : 'spline',
      id: `actual-${resolution}`, // id is needed so that Highcharts can update the series correctly
      custom: { type: ForecastSeriesTypesEnum.AGGREGATION },
      allowPointSelect: false,
    });

    return actuals;
  }

  private getIncompleteActuals(
    dataSeries: DataSeries,
    resolution: Resolution,
  ): SeriesOptionsType[] {
    const actuals: SeriesOptionsType[] = [];

    // only show main series if no filter combination is active
    if (this.areFilterCombinationsActive()) {
      return actuals;
    }

    // incomplete truth chart for now only for yearly resolution
    if (dataSeries.values.incompleteTruth && resolution === Resolution.Yearly) {
      const filteredActuals = dataSeries.values.data.filter(
        (value) => value[1] !== null,
      );
      const incompleteTruthValue = filteredActuals.slice(-1);
      if (incompleteTruthValue[0][1]) {
        // show last truth value as striped bar indicating the time period is still ongoing and thus the truth value is not completed yet
        actuals.push({
          name: this.translationService.instant(
            'RESULT.CHART.INCOMPLETE_TRUTH',
          ),
          data: incompleteTruthValue,
          visible: dataSeries.values.visible,
          color: this.getIncompleteColor(dataSeries.colors[0]),
          showInNavigator: false,
          linkedTo: `actual-${resolution}`,
          type: 'column',
          id: `incomplete-truth-${resolution}`,
          custom: { type: ForecastSeriesTypesEnum.AGGREGATION },
        });
      }
    }
    return actuals;
  }

  private getAdditionalTimeSeries(
    dataSeries: DataSeries,
    resolution: Resolution,
  ): SeriesOptionsType[] {
    const additionalTimeSeries: SeriesOptionsType[] = [];
    const timeSeries = this.additionalTimeSeries();
    if (
      dataSeries?.additionalTimeSeries &&
      Object.keys(dataSeries.additionalTimeSeries).length > 0 &&
      timeSeries.length > 0 &&
      timeSeries[0].length > 0
    ) {
      const numberOfAdditionalTimeSeries = Object.keys(
        dataSeries.additionalTimeSeries,
      ).length;
      const additionalTimeSeriesColors =
        this.chartColorService.getAdditionalTimeSeriesColors(
          numberOfAdditionalTimeSeries,
        );

      Object.keys(dataSeries.additionalTimeSeries).forEach((key, i) => {
        additionalTimeSeries.push({
          id: `additional-time-series-${resolution}-${key}`,
          type: resolution === Resolution.Yearly ? 'column' : 'spline',
          name: key,
          data: dataSeries.additionalTimeSeries[key],
          visible: true,
          color: additionalTimeSeriesColors[i],
          showInNavigator: false,
          allowPointSelect: false,
        });
      });
    }
    return additionalTimeSeries;
  }

  private getFilterCombinationForecastSeries(
    filterCombinationForecastSeries: Record<
      string,
      UpdatedForecastSeriesResponse
    >,
  ): SeriesOptionsType[] {
    const resolution = this.forecastResolution() ?? Resolution.Yearly;
    const savedFilterCombinations = this.savedFilterCombinations();

    let seriesWithFilterCombinationSeries: SeriesOptionsType[] = [];

    // iterate over all filter combinations
    Object.keys(filterCombinationForecastSeries).forEach((key, i) => {
      const props: FilterCombinationForecastSeriesProps = {
        series: seriesWithFilterCombinationSeries,
        filterCombinationForecastSeries: filterCombinationForecastSeries[key],
        filterCombinationId: key,
        resolution,
        savedFilterCombinations,
        // we dont want the first selected color to be used for the filter combination series cause it is already used for the main series
        color: this.chartColorService.getColor(i),
      };
      // 1. add predictions
      seriesWithFilterCombinationSeries =
        this.addFilterCombinationSeriesPredictions(props);

      // 2. add confidences
      seriesWithFilterCombinationSeries =
        this.addFilterCombinationSeriesConfidences(props);

      // 3. add actuals
      seriesWithFilterCombinationSeries =
        this.addFilterCombinationSeriesActuals(props);
    });
    return seriesWithFilterCombinationSeries;
  }

  private addFilterCombinationSeriesPredictions(
    props: FilterCombinationForecastSeriesProps,
  ): SeriesOptionsType[] {
    const {
      filterCombinationForecastSeries,
      filterCombinationId,
      resolution,
      savedFilterCombinations,
      series,
      color,
    } = props;

    // prediction chart
    if (filterCombinationForecastSeries.predictions) {
      series.push({
        ...structuredClone(filterCombinationForecastSeries.predictions),

        name: `${
          savedFilterCombinations.find((n) => n.id === filterCombinationId)
            ?.name || filterCombinationId
        } ${this.translationService.instant('RESULT.CHART.PREDICTION')}`,
        color: this.transformColorForType(color, 'prediction'),
        showInNavigator: true,
        type: resolution === Resolution.Yearly ? 'column' : 'spline',
        id: this.getSeriesIdByFilterCombinationId(
          filterCombinationId,
          'prediction',
          resolution,
        ),
        dashStyle: resolution !== Resolution.Yearly ? 'ShortDot' : undefined,
        custom: { type: ForecastSeriesTypesEnum.AGGREGATION },
        marker: {
          fillColor: color,
          states: {
            hover: {
              fillColor: color,
            },
            select: {
              fillColor: color,
            },
          },
        },
        point: {
          events: {
            click: (event: PointClickEventObject) => {
              this.clickedPredictionPoint.emit({
                date: event.point.x,
                filterCombinationId,
                color,
              });
            },
          },
        },
      });
    }

    return series;
  }

  private addFilterCombinationSeriesActuals(
    props: FilterCombinationForecastSeriesProps,
  ): SeriesOptionsType[] {
    const {
      filterCombinationForecastSeries,
      filterCombinationId,
      resolution,
      savedFilterCombinations,
      series,
      color,
    } = props;
    series.push({
      name: `${
        savedFilterCombinations.find((n) => n.id === filterCombinationId)
          ?.name || filterCombinationId
      } ${this.translationService.instant('RESULT.CHART.REAL')}`,
      data: filterCombinationForecastSeries.values.incompleteTruth
        ? filterCombinationForecastSeries.values.data
            .filter((value) => value[1] !== null)
            .slice(0, -1)
        : filterCombinationForecastSeries.values.data, // remove last truth value if it is incomplete
      visible: true,
      zIndex: 1,
      color: this.transformColorForType(color, 'actual'),
      showInNavigator: true,
      type: resolution === Resolution.Yearly ? 'column' : 'spline',
      id: this.getSeriesIdByFilterCombinationId(
        filterCombinationId,
        'actual',
        resolution,
      ),
      allowPointSelect: false,
      custom: { type: ForecastSeriesTypesEnum.AGGREGATION },
      marker: {
        fillColor: color,
        states: {
          hover: {
            fillColor: color,
          },
          select: {
            fillColor: color,
          },
        },
      },
    });

    // incomplete truth chart for now only for yearly resolution
    if (
      filterCombinationForecastSeries.values.incompleteTruth &&
      resolution === Resolution.Yearly
    ) {
      const filteredActuals =
        filterCombinationForecastSeries.values.data.filter(
          (value) => value[1] !== null,
        );
      const incompleteTruthValue = filteredActuals.slice(-1);
      if (incompleteTruthValue[0][1]) {
        // show last truth value as striped bar indicating the time period is still ongoing and thus the truth value is not completed yet
        series.push({
          name: `${
            savedFilterCombinations.find((n) => n.id === filterCombinationId)
              ?.name || filterCombinationId
          } ${this.translationService.instant('RESULT.CHART.INCOMPLETE_TRUTH')}`,
          data: incompleteTruthValue,
          linkedTo: this.getSeriesIdByFilterCombinationId(
            filterCombinationId,
            'actual',
            resolution,
          ),
          color: this.getIncompleteColor(color),
          visible: true,
          showInNavigator: false,
          type: 'column',
          id: this.getSeriesIdByFilterCombinationId(
            filterCombinationId,
            'incomplete',
            resolution,
          ),
          custom: { type: ForecastSeriesTypesEnum.AGGREGATION },
        });
      }
    }
    return series;
  }

  private addFilterCombinationSeriesConfidences(
    props: FilterCombinationForecastSeriesProps,
  ): SeriesOptionsType[] {
    const {
      filterCombinationForecastSeries,
      resolution,
      series,
      filterCombinationId,
      savedFilterCombinations,
      color,
    } = props;

    if (
      filterCombinationForecastSeries.confidences &&
      this.isIntervalEnabled()
    ) {
      series.push({
        ...filterCombinationForecastSeries.confidences,
        name: `${
          savedFilterCombinations.find((n) => n.id === filterCombinationId)
            ?.name || filterCombinationId
        } ${this.translationService.instant('RESULT.CHART.CONFIDENCE_INTERVAL')}`,
        linkedTo: this.getSeriesIdByFilterCombinationId(
          filterCombinationId,
          'prediction',
          resolution,
        ),
        color:
          resolution === Resolution.Yearly
            ? this.transformColorForType(color, 'confidence')
            : color,
        opacity: resolution === Resolution.Yearly ? 1 : 0.3,
        type: resolution === Resolution.Yearly ? 'errorbar' : 'areasplinerange',
        marker: {
          enabled: false,
          fillColor: color,
          states: {
            hover: {
              enabled: false,
              fillColor: color,
            },
            select: {
              fillColor: color,
            },
          },
        },
        allowPointSelect: false,
        id: this.getSeriesIdByFilterCombinationId(
          filterCombinationId,
          'confidence',
          resolution,
        ),
      });
    }

    return series;
  }

  private getSeriesIdByFilterCombinationId(
    filterCombinationId: string,
    type: 'confidence' | 'prediction' | 'actual' | 'incomplete',
    resolution: Resolution,
  ): string | undefined {
    switch (type) {
      case 'actual':
        return `actual__${resolution}__filter__combination__${filterCombinationId}`;
      case 'prediction':
        return `prediction__${resolution}__filter__combination__${filterCombinationId}`;
      case 'confidence':
        return `confidence__${resolution}__filter__combination__${filterCombinationId}`;
      case 'incomplete':
        return `incomplete__${resolution}__filter__combination__${filterCombinationId}`;
      default:
        console.error('could not determine type of series');
        return undefined;
    }
  }

  private transformColorForType(
    hex: string,
    type: 'confidence' | 'prediction' | 'actual' | 'incomplete',
  ): string {
    const color = Color(hex);
    switch (type) {
      case 'actual':
        return color.hexa();
      case 'prediction':
        return color.alpha(0.6).hexa();
      // only to use for yearly resolution, otherwise the color should be the exact same as the input color
      case 'incomplete':
        return color.isDark()
          ? color.alpha(0.6).lighten(0.3).hexa()
          : color.alpha(0.7).darken(0.2).hexa();
      case 'confidence':
      default:
        // basically black for yearly confidences
        return color.darken(20).hexa();
    }
  }
  private getIncompleteColor(color: string): PatternObject {
    return {
      pattern: {
        path: {
          d: 'M 0 10 L 10 0 M 9 11 L 11 9 M -1 1 L 1 -1',
          strokeWidth: 2,
        },
        width: 10,
        height: 10,
        opacity: 1,
        color: this.transformColorForType(color, 'incomplete'),
        backgroundColor: color,
      },
    };
  }

  private areFilterCombinationsActive() {
    const activeFilterCombinations = this.filterCombinationTimeSeries();
    return Object.keys(activeFilterCombinations).length > 0;
  }
}
